/**
 * O ARQUIVO DO DOCUMENTO E A PROVA DA REGULARIZAÇÃO (migração 0090, módulo 16,
 * ferramenta 4) — o que a mesa e o celular dizem e fazem com ele.
 *
 * O caminho é o do anexo da O.S.: o tipo é lido do conteúdo, a imagem chega
 * sem EXIF e sem a localização, e a leitura é por uma URL de cinco minutos.
 * O arquivo não se edita nem se apaga: o documento renovado ganha arquivo
 * novo, e o antigo fica com a validade que tinha.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatarDia, formatarInteiro } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

import { api, type ArquivoDoDocumento, type DocumentoNaLista, type FinalidadeDoArquivo } from './api';

export { explicarFalha };

/** O mesmo teto do servidor. Conferir aqui poupa subir 200 MB para ouvir "não". */
export const TAMANHO_MAXIMO_DO_ARQUIVO = 100 * 1024 * 1024;

export const ROTULO_DA_FINALIDADE: Readonly<Record<FinalidadeDoArquivo, string>> = {
  documento: 'Cópia do documento',
  regularizacao: 'Prova da regularização',
};

/** "2 arquivos · 1 prova", "Sem arquivo". Ausente em servidor anterior: nada a dizer. */
export function contagemDosArquivos(d: Pick<DocumentoNaLista, 'arquivos' | 'evidencias'>): string | null {
  if (d.arquivos === undefined && d.evidencias === undefined) return null;
  const arquivos = d.arquivos ?? 0;
  const provas = d.evidencias ?? 0;
  if (arquivos === 0 && provas === 0) return 'Sem arquivo';
  return [
    arquivos > 0 ? `${formatarInteiro(arquivos)} ${arquivos === 1 ? 'arquivo' : 'arquivos'}` : null,
    provas > 0 ? `${formatarInteiro(provas)} ${provas === 1 ? 'prova' : 'provas'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** "Cópia do documento · válido até 10/03/2027 · 02/10/2026 · Ana" — a linha de apoio. */
export function linhaDoArquivo(a: ArquivoDoDocumento): string {
  return [
    ROTULO_DA_FINALIDADE[a.finalidade],
    a.validoAteNaEpoca ? `válido até ${formatarDia(a.validoAteNaEpoca)} na época` : null,
    formatarDia(a.enviadoEm),
    a.enviadoPor,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** O que falta para enviar, campo a campo, com o que fazer. */
export function problemasDoArquivo(arquivo: File | null, descricao: string): { readonly arquivo?: string; readonly descricao?: string } {
  return {
    ...(arquivo === null
      ? { arquivo: 'Escolha a foto ou o PDF do documento.' }
      : arquivo.size > TAMANHO_MAXIMO_DO_ARQUIVO
        ? { arquivo: 'O arquivo passa de 100 MB. Reduza a foto ou divida o PDF.' }
        : {}),
    ...(descricao.trim() === '' ? { descricao: 'Diga o que é o arquivo, por exemplo "CNH frente e verso".' } : {}),
  };
}

export function useArquivosDoDocumento(documentoId: string | null) {
  return useQuery({
    queryKey: ['pessoas', 'arquivos', documentoId],
    enabled: documentoId !== null,
    queryFn: async () => (await api.arquivos(documentoId ?? '')).anexos,
  });
}

interface PedidoDoArquivo {
  readonly documento: Pick<DocumentoNaLista, 'id' | 'tipoCodigo' | 'tipo' | 'pessoa' | 'equipamento'>;
  readonly arquivo: File;
  readonly descricao: string;
  readonly finalidade: FinalidadeDoArquivo;
}

export function useAnexarAoDocumento() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (v: PedidoDoArquivo) => api.anexar(v.documento.id, v.arquivo, v.descricao, v.finalidade),
    meta: {
      retorno: retornoDe<unknown, PedidoDoArquivo>((_r, v) => ({
        titulo: v.finalidade === 'regularizacao' ? 'Prova da regularização guardada' : 'Arquivo do documento guardado',
        alvo: { codigo: v.documento.tipoCodigo, nome: v.documento.pessoa ?? v.documento.equipamento ?? v.documento.tipo },
        fatos: [v.descricao, v.arquivo.name],
        efeito: 'Fica com a validade de hoje, e não se apaga',
      })),
      /* A recusa aparece no formulário, e não de novo num toast. */
      silenciarFalha: true,
    },
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['pessoas'] }),
  });
}

/**
 * Abre o arquivo numa aba nova. A aba abre ANTES do pedido da URL, no clique:
 * aberta depois de um `await`, o navegador a trataria como janela não pedida.
 * É o mesmo jeito do anexo da O.S.
 */
export async function abrirArquivoDoDocumento(anexoId: string): Promise<void> {
  const aba = window.open('about:blank', '_blank');
  try {
    const { url } = await api.urlDoArquivo(anexoId);
    if (aba) {
      aba.opener = null;
      aba.location.href = url;
    } else {
      window.location.assign(url);
    }
  } catch (erro) {
    aba?.close();
    throw erro;
  }
}
